<script>
import { mapActions } from 'vuex';

export default {
    data() {
        return {
            users: [],
            roles: [],
            departments: [],

            headers: [
                { key: 'name', flex: 4 },
                { key: 'email', flex: 6 },
                { key: 'role_name', flex: 4 },
                { key: 'user_department_name', flex: 4 },
                { key: 'carbon_viewer', flex: 1 },
                { key: 'carbon_editor', flex: 1 },
                { key: 'carbon_auditor', flex: 1 },
                { key: 'changes', flex: 2 }
            ]
        }
    },

    mounted() {
        this._getTeamMembers();
    },

    methods: {
        ...mapActions('settings', [
            'getTeamMembers',
            'updateCarbonAssignment'
        ]),

        _getTeamMembers() {
            this.getTeamMembers()
                .then(res => {
                    this.users = res.data.team_members;
                    this.roles = res.data.roles;
                    this.departments = res.data.departments;
                    this.users.forEach(user => this.setUsersOriginal(
                        user, 
                        user.carbon_viewer, 
                        user.carbon_editor, 
                        user.carbon_auditor
                    ));
                })
        },

        _updateCarbonAssignment(user) {
            if (!user || !this.hasUserChanged(user))
                return;

            const params = {
                user_id: user.user_id,
                assignments: {
                    view: user.carbon_viewer,
                    edit: user.carbon_editor,
                    audit: user.carbon_auditor
                }
            };

            this.updateCarbonAssignment(params)
                .then(() => {
                    this.setUsersOriginal(
                        user, 
                        params.assignments.view,
                        params.assignments.edit,
                        params.assignments.audit
                    )
                    this.$notify('success', '', true);
                })
                .catch(() => {
                    this.$notify('error', '', true);
                })
        },

        setUsersOriginal(user, view, edit, audit) {
            let tick = user['current_tick'] != undefined ? user['current_tick'] + 1 : 0;
            this.$set(user, 'current_tick', tick);
            this.$set(user, '_original', {
                carbon_viewer: view,
                carbon_editor: edit,
                carbon_auditor: audit
            });
        },

        hasUserChanged(user) {
            let val = false;
            for (const key in user['_original']) {
                if (!Object.hasOwn(user['_original'], key)) continue;
                const originalAssignmentVal = user['_original'][key];
                if (originalAssignmentVal !== user[key]) {
                    val = true;
                    break;
                }
            }
            
            return val;
        },

        onAssignmentChange(e, user, key) {
            const val = e.target.checked;
            if ((key === 'carbon_editor' || key === 'carbon_auditor') && val == true) {
                user.carbon_viewer = true;
            } else if (key === 'carbon_viewer' && val == false) {
                user.carbon_editor = false;
                user.carbon_auditor = false;
            }
        },

        isCheckboxSetting(key) {
            return key === 'carbon_viewer' || key === 'carbon_editor' || key === 'carbon_auditor';
        },
    }
}
</script>

<template>
    <div class="flex-all-centered-col">
        <div class="group-title">
            {{ $t('carbon_assignments.team_members') }}
        </div>
        <div class="users-table flex-all-centered-col">
            <div class="header-row flex-all-centered-row">
                <div v-for="header in headers" :key="header.key" 
                    :style="{
                        'flex': header.flex
                    }"
                >
                    <span>{{ $t(`carbon_assignments.headers.${header.key}`) }}</span>
                </div>
            </div>
            <div v-for="user in users" :key="user.user_id" class="user-row flex-all-centered-row">
                <div v-for="header in headers" :key="header.key" 
                    :style="{
                        'flex': header.flex
                    }"
                    :class="{
                        'flex-all-centered-row': isCheckboxSetting(header.key) || header.key === 'changes'
                    }"
                >
                    <div v-if="header.key === 'name'">
                        {{ user.firstname }} {{ user.surname }}
                    </div>
                    <input v-else-if="isCheckboxSetting(header.key)" type="checkbox"
                        v-model="user[header.key]"
                        @change="onAssignmentChange($event, user, header.key)"
                    >
                    <button v-else-if="header.key === 'changes'" 
                        class="sust-btn sust-btn-green"
                        :disabled="!hasUserChanged(user)"
                        @click="_updateCarbonAssignment(user)"
                        :key="user['current_tick']"
                    >
                        {{ $t('userProfile.confirm') }}
                    </button>
                    <div v-else>
                        {{ user[header.key] }}
                    </div>
                </div>

            </div>
            <div v-if="users.length === 0" class="user-row flex-all-centered-row"
                style="justify-content: center !important; padding: .5em;">
                {{ $t('carbon_assignments.no_members') }}
            </div>
        </div>
    </div>
</template>

<style lang='scss' scoped>
    .users-table {
        border-radius: 1em;
        overflow: visible !important;
        box-shadow: 0 .25em .5em .1em gray;
    }

    .group-title {
        color: black;
        font-weight: bold;
        font-size: 1.2em;
        text-align: left;
        padding: .35em;
        width: 100%;
    }

    .header-row {
        overflow: hidden !important;
        background-color: var(--sustainfinity-navy);
        color: whitesmoke;
        border-top-left-radius: 1em;
        border-top-right-radius: 1em;

        > div {
            border-right: thin solid whitesmoke;
            &:last-of-type {
                border-right: 0 !important;
            }
        }
    }
    
    .user-row {
        position: relative;
        overflow: visible !important;
        background-color: whitesmoke;
        color: var(--sustainfinity-navy);
        border-bottom: thin solid lightgray;

        &:last-of-type {
            border-bottom: 0 !important;
            border-bottom-left-radius: 1em;
            border-bottom-right-radius: 1em;
        }

        > div {
            &:last-of-type {
                border-right: 0 !important;
            }
        }
    }

    .header-row, .user-row {
        justify-content: flex-start;
        align-items: stretch;
        > div {
            padding: 1em;
        }
    }
</style>